import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import MinimizeRoundedIcon from '@mui/icons-material/MinimizeRounded'
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { subscribePortalVoiceCallState } from 'src/mentor/components/voice/portalVoiceCallManager'
import { getPortalVoiceOptions } from 'src/mentor/services/portalVoiceCallService'
import { getSupportAgentContactCapabilities } from 'src/supportPortal/services/supportAgentContactService'
import SupportEmailComposerDialog from 'src/supportPortal/components/contact/SupportEmailComposerDialog'
import SupportCallControls from 'src/supportPortal/components/call/SupportCallControls'
import SupportCallHistoryPanel from 'src/supportPortal/components/call/SupportCallHistoryPanel'
import SupportCallTranscriptPanel from 'src/supportPortal/components/call/SupportCallTranscriptPanel'
import {
  hasDialablePhone,
  maskPhone,
  normalizeDialablePhone,
} from 'src/supportPortal/components/call/supportCallContact'
import {
  getSupportCallTranscriptText,
  setSupportCallTranscriptionContext,
} from 'src/supportPortal/components/call/supportCallTranscription'
import { logSupportCallOutcome } from 'src/supportPortal/services/supportCallService'

const CALL_OUTCOMES = [
  'Connected — resolved',
  'Connected — callback needed',
  'No answer',
  'Busy',
  'Wrong number',
  'Switched off / unreachable',
]

const LIVE_STATUSES = ['connecting', 'ringing', 'connected', 'reconnecting', 'on-hold', 'ending']

// The expanded, page-level call surface. Rendered once by SupportCallProvider (above the router
// outlet) so it survives navigation. Loads routes + capabilities ONLY on open — no per-card
// polling. Closing while a call is live minimizes to the PIP instead of dropping the call.
export default function SupportCallSurface({ contact, onMinimize, onClose }) {
  const [callState, setCallState] = useState(null)
  const [routesState, setRoutesState] = useState({ loading: false, routes: [], error: '' })
  const [capabilities, setCapabilities] = useState(null)
  const [phoneRevealed, setPhoneRevealed] = useState(false)
  const [activeTab, setActiveTab] = useState('activity')
  const [emailOpen, setEmailOpen] = useState(false)
  const [outcome, setOutcome] = useState('')
  const [note, setNote] = useState('')
  const [savingOutcome, setSavingOutcome] = useState(false)
  const lastCallRef = useRef({ callSessionId: '', durationSeconds: 0 })

  useEffect(() => subscribePortalVoiceCallState(setCallState), [])

  const callContext = useMemo(
    () => ({
      patientId: contact?.patientId || undefined,
      practitionerId: contact?.practitionerId || undefined,
      bookedEventId: contact?.bookedEventId || undefined,
      clinicBrandId: contact?.clinicBrandId || undefined,
      clinicBrandName: contact?.clinicBrandName || undefined,
      clinicId: contact?.clinicId || undefined,
      targetRole: contact?.targetRole || 'patient',
      eventType: contact?.eventType || undefined,
    }),
    [contact],
  )

  const loadRoutes = useMemo(
    () => async () => {
      if (!contact?.patientId && !contact?.practitionerId) {
        return
      }
      setRoutesState((current) => ({ ...current, loading: true, error: '' }))
      const result = await getPortalVoiceOptions({
        actorRole: 'support-agent',
        bookedEventId: contact?.bookedEventId,
        clinicBrandId: contact?.clinicBrandId,
        clinicId: contact?.clinicId,
        practitionerId: contact?.practitionerId,
        patientId: contact?.patientId,
        targetRole: contact?.targetRole || 'patient',
        eventType: contact?.eventType || undefined,
      })
      setRoutesState({
        loading: false,
        routes: result?.routes || [],
        error: result?.ok ? '' : result?.message || 'Unable to load call routes.',
      })
    },
    [contact],
  )

  // Load-on-open: routes + capabilities fetched when the surface opens for a contact.
  useEffect(() => {
    let active = true
    if (!contact) {
      return undefined
    }
    void loadRoutes()
    ;(async () => {
      const result = await getSupportAgentContactCapabilities(callContext)
      if (active && result?.ok) {
        setCapabilities(result)
      }
    })()
    return () => {
      active = false
    }
  }, [contact, callContext, loadRoutes])

  // Reset per-contact UI when the target changes.
  useEffect(() => {
    setPhoneRevealed(false)
    setActiveTab('activity')
    setOutcome('')
    setNote('')
    lastCallRef.current = { callSessionId: '', durationSeconds: 0 }
    setSupportCallTranscriptionContext(null)
  }, [contact?.bookedEventId, contact?.patientId, contact?.practitionerId])

  // Track the live call session so the disposition + transcript persistence attach to it, and feed
  // the transcription module the callSessionId so finalized segments persist server-side.
  useEffect(() => {
    const callSessionId = callState?.interactionId || ''
    if (!callSessionId) {
      return
    }
    lastCallRef.current = {
      callSessionId,
      durationSeconds: callState?.elapsedSeconds || lastCallRef.current.durationSeconds || 0,
    }
    setSupportCallTranscriptionContext({
      callSessionId,
      bookedEventId: contact?.bookedEventId,
      patientId: contact?.patientId,
      practitionerId: contact?.practitionerId,
    })
  }, [callState?.interactionId, callState?.elapsedSeconds, contact?.bookedEventId, contact?.patientId, contact?.practitionerId])

  const status = callState?.status || 'idle'
  const isLive = LIVE_STATUSES.includes(status)

  const displayPhone = useMemo(() => {
    const raw = capabilities?.targetPhoneNumber || contact?.rawPhone || ''
    if (!raw) {
      return 'No number on file'
    }
    return phoneRevealed ? normalizeDialablePhone(raw) : maskPhone(raw)
  }, [capabilities?.targetPhoneNumber, contact?.rawPhone, phoneRevealed])

  const whatsappNumber = capabilities?.targetWhatsappNumber || contact?.rawPhone || ''
  const emailEnabled = capabilities?.channels?.email?.enabled !== false && !!capabilities?.targetEmail
  const whatsappEnabled =
    capabilities?.channels?.whatsapp?.enabled !== false && hasDialablePhone(whatsappNumber)

  function handleCloseClick() {
    if (isLive) {
      onMinimize?.()
      toast.info('Call moved to the mini-player — it stays connected.')
      return
    }
    onClose?.()
  }

  function handleWhatsapp() {
    const digits = normalizeDialablePhone(whatsappNumber).replace(/^\+/, '')
    if (!digits) {
      toast.error('No WhatsApp number on file.')
      return
    }
    window.open(`https://wa.me/${digits}`, '_blank', 'noopener')
  }

  function handleCopySummary() {
    const transcript = getSupportCallTranscriptText()
    const summary = [
      `Contact: ${contact?.name || 'Patient'}`,
      outcome ? `Outcome: ${outcome}` : '',
      note ? `Note: ${note}` : '',
      transcript ? `\nTranscript:\n${transcript}` : '',
    ]
      .filter(Boolean)
      .join('\n')
    if (!summary.trim()) {
      toast.info('Add an outcome or note first.')
      return
    }
    navigator.clipboard
      .writeText(summary)
      .then(() => toast.success('Call summary copied'))
      .catch(() => toast.error('Could not copy the summary'))
  }

  async function handleSaveOutcome() {
    if (!outcome && !note.trim()) {
      toast.info('Add an outcome or note first.')
      return
    }
    setSavingOutcome(true)
    const result = await logSupportCallOutcome({
      ...callContext,
      callSessionId: lastCallRef.current.callSessionId || undefined,
      durationSeconds: lastCallRef.current.durationSeconds || undefined,
      direction: 'outbound',
      maskedNumber: maskPhone(capabilities?.targetPhoneNumber || contact?.rawPhone),
      outcome: outcome || 'Call logged',
      note,
    })
    setSavingOutcome(false)
    if (result?.ok) {
      toast.success('Call outcome saved')
    } else {
      toast.error(result?.message || 'Could not save the outcome')
    }
  }

  if (!contact) {
    return null
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1250,
          width: { xs: '100%', sm: 420 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          boxShadow: '-18px 0 48px rgba(15, 23, 42, 0.18)',
          borderLeft: '1px solid rgba(226, 232, 240, 0.9)',
          animation: 'supportCallSlideIn 200ms ease',
          '@keyframes supportCallSlideIn': {
            from: { transform: 'translateX(24px)', opacity: 0 },
            to: { transform: 'translateX(0)', opacity: 1 },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.75, background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)', color: '#fff' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.16)',
                  flexShrink: 0,
                }}
              >
                <PhoneInTalkRoundedIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }} noWrap>
                  {contact?.name || 'Patient'}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.3, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={(contact?.targetRole || 'patient') === 'patient' ? 'Patient' : 'Practitioner'}
                    sx={{ height: 18, fontSize: 10, fontWeight: 700, color: '#fff', backgroundColor: 'rgba(255,255,255,0.2)' }}
                  />
                  {contact?.clinicBrandName ? (
                    <Typography sx={{ fontSize: 11.5, opacity: 0.9 }} noWrap>
                      {contact.clinicBrandName}
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.25}>
              <Tooltip title="Minimize to mini-player">
                <IconButton size="small" onClick={() => onMinimize?.()} sx={{ color: '#fff' }}>
                  <MinimizeRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={isLive ? 'Minimize (keeps the call)' : 'Close'}>
                <IconButton size="small" onClick={handleCloseClick} sx={{ color: '#fff' }}>
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.25 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '0.02em' }}>{displayPhone}</Typography>
            {(capabilities?.targetPhoneNumber || contact?.rawPhone) ? (
              <IconButton size="small" onClick={() => setPhoneRevealed((value) => !value)} sx={{ color: 'rgba(255,255,255,0.85)' }}>
                {phoneRevealed ? <VisibilityOffRoundedIcon sx={{ fontSize: 16 }} /> : <VisibilityRoundedIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            ) : null}
          </Stack>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <SupportCallControls
            callState={callState}
            contact={contact}
            routes={routesState.routes}
            routesLoading={routesState.loading}
            routesError={routesState.error}
            onReloadRoutes={loadRoutes}
          />

          {/* Quick actions */}
          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<WhatsAppIcon />}
              onClick={handleWhatsapp}
              disabled={!whatsappEnabled}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                borderColor: 'rgba(18, 140, 126, 0.5)',
                color: '#128c7e',
                '&:hover': { borderColor: '#128c7e', backgroundColor: 'rgba(18, 140, 126, 0.06)' },
              }}
            >
              WhatsApp
            </Button>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<EmailRoundedIcon />}
              onClick={() => setEmailOpen(true)}
              disabled={!emailEnabled}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                borderColor: 'rgba(37, 99, 235, 0.4)',
                color: '#2563eb',
                '&:hover': { borderColor: '#2563eb', backgroundColor: 'rgba(37, 99, 235, 0.06)' },
              }}
            >
              Email
            </Button>
          </Stack>

          {/* Tabs */}
          <Box sx={{ borderBottom: '1px solid rgba(226, 232, 240, 0.9)' }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="fullWidth"
              sx={{
                minHeight: 38,
                '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 700, fontSize: 12.5 },
                '& .Mui-selected': { color: '#4f46e5 !important' },
                '& .MuiTabs-indicator': { backgroundColor: '#4f46e5' },
              }}
            >
              <Tab value="activity" label="Activity" />
              <Tab value="transcript" label="Transcript" />
              <Tab value="notes" label="Notes" />
            </Tabs>
          </Box>

          <Box sx={{ flex: 1, minHeight: 200 }}>
            {activeTab === 'activity' ? (
              <SupportCallHistoryPanel
                patientId={contact?.patientId}
                practitionerId={contact?.practitionerId}
                bookedEventId={contact?.bookedEventId}
                clinicBrandId={contact?.clinicBrandId}
                clinicId={contact?.clinicId}
                targetRole={contact?.targetRole}
                eventType={contact?.eventType}
              />
            ) : null}
            {activeTab === 'transcript' ? <SupportCallTranscriptPanel callActive={isLive} /> : null}
            {activeTab === 'notes' ? (
              <Stack spacing={1.25}>
                <TextField
                  select
                  size="small"
                  label="Call outcome"
                  value={outcome}
                  onChange={(event) => setOutcome(event.target.value)}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-root': { borderRadius: '10px', fontSize: 13 } }}
                >
                  <option value="">Select an outcome…</option>
                  {CALL_OUTCOMES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Notes"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  multiline
                  minRows={4}
                  placeholder="Disposition, what was discussed, next steps…"
                  sx={{ '& .MuiInputBase-root': { borderRadius: '10px', fontSize: 13 } }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveOutcome}
                    disabled={savingOutcome}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: '10px',
                      boxShadow: 'none',
                      backgroundColor: '#4f46e5',
                      '&:hover': { backgroundColor: '#4338ca', boxShadow: 'none' },
                    }}
                  >
                    {savingOutcome ? 'Saving…' : 'Save outcome'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCopySummary}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}
                  >
                    Copy summary
                  </Button>
                </Stack>
                <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>
                  Saved to the support communication log (voice / call-outcome) and shown in Activity.
                </Typography>
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Box>

      <SupportEmailComposerDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        context={callContext}
        targetName={contact?.name}
        targetEmail={capabilities?.targetEmail}
      />
    </>
  )
}
